import { FC, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { Elements } from '@stripe/react-stripe-js';

import { Layout } from '@/layout';

import {
  Dashboard,
  Tariffs,
  Investigations,
  InvestigationDetails,
  Profile,
  Notifications,
  AmlCheckDetails,
  SignUp,
  SignUpSuccess,
  SignIn,
  ConfirmEmail,
  ConfirmEmailSuccess,
  PasswordForgot,
  PasswordForgotSuccess,
  PasswordReset,
  PasswordResetSuccess,
  InvestigationResultViewer,
  Banners,
  Contact,
  ReminderUnsubscribe,
  ComplianceDashboardOverview,
  ComplianceDashboardUsers,
  ComplianceDashboardAlertsSettings,
  B2BRequest,
  OtpAuth,
  RefSignUp,
  TraceSignUp,
  TraceInvestigation,
  TraceInvestigationDetails,
} from '@/pages';

import { Suspense, RequireAuth, PaymentRedirect } from '@/components';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useHelpCrunchAttribution } from '@/hooks';
import { profileSelectors, userSelectors } from '@/redux/selectors';
import { externalServiceActions, userActions } from '@/redux/actions';
import { PaymentProvider } from '@/contexts';
import { navigationPages, stripePromise } from '@/config';
import { HubspotProvider } from '@aaronhayes/react-use-hubspot-form';

import './App.module.sass';
import { ComplianceDashboardAddressesMonitoring } from '@/pages/ComplianceDashboard/AddressesMonitoring/AddressesMonitoring';
import OverviewCustomers from '@/pages/ComplianceDashboard/Overview/Customers/Customers';
import OverviewTransfers from '@/pages/ComplianceDashboard/Overview/Transfers/Transfers';
import OverviewAlerts from '@/pages/ComplianceDashboard/Overview/Alerts/Alerts';

interface AppProps {}

const App: FC<AppProps> = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(userActions.check());
  }, []);

  useHelpCrunchAttribution();

  useEffect(() => {
    document.body.addEventListener('HelpcrunchLoaded', (e: Event) => {
      const isLoaded = Boolean((e as CustomEvent).detail);
      dispatch(externalServiceActions.setHelpcrunchLoaded(isLoaded));
    });

    return () =>
      document.body.removeEventListener('HelpcrunchLoaded', () => {});
  }, []);

  const suspense = useAppSelector(userSelectors.getSuspense);

  const isComplianceDashboardAvailable = useMemo(
    () =>
      import.meta.env.VITE_REACT_APP_COMPLIANCE_AVAILABLE?.toLowerCase() ===
      'true',
    [],
  );

  const isModeLimited = useAppSelector(profileSelectors.checkIfModeLimited);

  const isCheckOnly = useAppSelector(userSelectors.checkIfCheckOnly);
  const isAiTraceOnly = useAppSelector(userSelectors.checkIfAiTraceOnly);
  const isAiTraceIncluded = useAppSelector(
    userSelectors.checkIfAiTraceIncluded,
  );

  const startingPagePath = useMemo(() => {
    if (isAiTraceOnly || isAiTraceIncluded) {
      return navigationPages.TRACE_INVESTIGATIONS.path;
    }

    if (isModeLimited || !isComplianceDashboardAvailable || isCheckOnly) {
      return navigationPages.DASHBOARD.path;
    }
    return (
      navigationPages.COMPLIANCE_DASHBOARD.subMenu?.TRANSACTION_MONITORING
        .subMenu?.OVERVIEW.path ?? ''
    );
  }, [
    isModeLimited,
    isComplianceDashboardAvailable,
    isCheckOnly,
    isAiTraceOnly,
    isAiTraceIncluded,
  ]);

  if (suspense) {
    return <Suspense />;
  }

  return (
    <>
      <Helmet>
        <title>Web - AMLBot</title>
        <meta
          name="description"
          content="AMLBot: WEB solution for cryptocurrency transaction monitoring and AML compliance. Protect your business from risks."
        />
      </Helmet>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route
            index
            path="/"
            element={<Navigate to={startingPagePath} replace />}
          />

          <Route
            path={navigationPages.DASHBOARD.path}
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route
            path={navigationPages.INVESTIGATIONS.path}
            element={
              <RequireAuth>
                <Investigations />
              </RequireAuth>
            }
          />

          <Route
            path={navigationPages.TRACE_INVESTIGATIONS.path}
            element={
              <RequireAuth>
                <TraceInvestigation />
              </RequireAuth>
            }
          />

          <Route
            path={navigationPages.TRACE_INVESTIGATION_DETAILS.path}
            element={
              <RequireAuth>
                <TraceInvestigationDetails />
              </RequireAuth>
            }
          />

          <Route
            path={navigationPages.PROFILE.path}
            element={
              <RequireAuth>
                <Profile />
              </RequireAuth>
            }
          />

          <Route
            path={navigationPages.NOTIFICATIONS.path}
            element={
              <RequireAuth>
                <Notifications />
              </RequireAuth>
            }
          />

          {isComplianceDashboardAvailable && !isCheckOnly && (
            <>
              <Route
                path={navigationPages.COMPLIANCE_DASHBOARD.path}
                element={
                  <Navigate
                    to={
                      navigationPages.COMPLIANCE_DASHBOARD.subMenu
                        ?.TRANSACTION_MONITORING.subMenu?.OVERVIEW.path ?? ''
                    }
                  />
                }
              />

              <Route
                path={
                  navigationPages.COMPLIANCE_DASHBOARD.subMenu
                    ?.TRANSACTION_MONITORING.path
                }
                element={
                  <Navigate
                    to={
                      navigationPages.COMPLIANCE_DASHBOARD.subMenu
                        ?.TRANSACTION_MONITORING.subMenu?.OVERVIEW.path ?? ''
                    }
                    replace
                  />
                }
              />

              <Route
                path={
                  navigationPages.COMPLIANCE_DASHBOARD.subMenu
                    ?.TRANSACTION_MONITORING.subMenu?.OVERVIEW.path
                }
                element={
                  <RequireAuth>
                    <ComplianceDashboardOverview />
                  </RequireAuth>
                }
              >
                <Route index element={<Navigate to="customers" replace />} />
                <Route path="customers" element={<OverviewCustomers />} />
                <Route path="transfers" element={<OverviewTransfers />} />
                <Route path="alerts" element={<OverviewAlerts />} />
              </Route>
              <Route
                path={
                  navigationPages.COMPLIANCE_DASHBOARD.subMenu
                    ?.TRANSACTION_MONITORING.subMenu?.USERS.path
                }
                element={
                  <RequireAuth>
                    <ComplianceDashboardUsers />
                  </RequireAuth>
                }
              />

              <Route
                path={
                  navigationPages.COMPLIANCE_DASHBOARD.subMenu
                    ?.TRANSACTION_MONITORING.subMenu?.ALERTS_SETTINGS.path
                }
                element={
                  <RequireAuth>
                    <ComplianceDashboardAlertsSettings />
                  </RequireAuth>
                }
              />
            </>
          )}

          <Route
            path={
              navigationPages.COMPLIANCE_DASHBOARD.subMenu?.ADDRESSES_MONITORING
                .path
            }
            element={
              <RequireAuth>
                <ComplianceDashboardAddressesMonitoring />
              </RequireAuth>
            }
          />

          <Route
            path={navigationPages.TARIFFS.path}
            element={
              <RequireAuth>
                <PaymentProvider>
                  <Tariffs />
                </PaymentProvider>
              </RequireAuth>
            }
          />
          <Route
            path={navigationPages.DETAILS.path}
            element={
              <RequireAuth>
                <AmlCheckDetails />
              </RequireAuth>
            }
          />
          <Route
            path={`${navigationPages.DETAILS.path}/:uid`}
            element={
              <RequireAuth>
                <AmlCheckDetails />
              </RequireAuth>
            }
          />
          <Route
            path={navigationPages.INVESTIGATION_DETAILS.path}
            element={
              <RequireAuth>
                <InvestigationDetails />
              </RequireAuth>
            }
          />
          <Route
            path="/contact"
            element={
              <RequireAuth>
                <Contact />
              </RequireAuth>
            }
          />

          <Route
            path="/signup"
            element={
              <HubspotProvider>
                <SignUp />
              </HubspotProvider>
            }
          />
          <Route path="/signup-success" element={<SignUpSuccess />} />

          <Route
            path="/signin"
            element={
              <HubspotProvider>
                <SignIn />
              </HubspotProvider>
            }
          />

          <Route path="/email/confirm" element={<ConfirmEmail />} />
          <Route
            path="/email/confirm-success"
            element={<ConfirmEmailSuccess />}
          />

          <Route path="/password/forgot" element={<PasswordForgot />} />
          <Route
            path="/password/forgot-success"
            element={<PasswordForgotSuccess />}
          />

          <Route path="/password/new" element={<PasswordReset />} />
          <Route
            path="/password/new-success"
            element={<PasswordResetSuccess />}
          />
          <Route
            path="/pay/:userData/:invoiceData"
            element={
              <Elements stripe={stripePromise}>
                <PaymentRedirect />
              </Elements>
            }
          />
          <Route path="/unsubscribe" element={<ReminderUnsubscribe />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Route>
        <Route
          path="/aml/response/:userData/:responseData"
          element={<InvestigationResultViewer />}
        />
        <Route path="/banners" element={<Banners />} />
        <Route path="/b2b_request" element={<B2BRequest />} />
        <Route
          path="/verifiwallet-signup"
          element={
            <HubspotProvider>
              <OtpAuth />
            </HubspotProvider>
          }
        />
        <Route
          path="/trace-signup"
          element={
            <HubspotProvider>
              <TraceSignUp />
            </HubspotProvider>
          }
        />
        <Route
          path="/ref-signup"
          element={
            <HubspotProvider>
              <RefSignUp />
            </HubspotProvider>
          }
        />
      </Routes>
    </>
  );
};

export default App;
